import Phaser from "phaser";
import {
  makeGlowTexture,
  makeGroundTexture,
  makeHazardTexture,
  makeHillsTexture,
  makeSkyTexture,
  makeTreeTexture,
} from "../textures";
import type { Ambience } from "../audio";
import { levelFor, LEVELS, type LevelConfig } from "../levels";
import { VIEW_HEIGHT, VIEW_WIDTH, WORLD_HEIGHT, WORLD_WIDTH } from "./dimensions";
import { advanceChain, chainActiveForLevel, CHAIN_CAP, CHAIN_WINDOW_MS, emptyChain, expireChain, resetChain, type ChainState } from "../chain";
import {
  REACH_MAX,
  REACH_MIN,
  REACH_START,
  reachReadiness,
  reachReady,
  restoreReach,
  spendReach,
  type MoteArrival,
} from "../reach";

const COLLECT_RADIUS = 45;
const HAZARD_RADIUS = 34;
const BEACON_RADIUS = 90;
const BEACON_X = WORLD_WIDTH * 0.86;
const BEACON_Y = WORLD_HEIGHT * 0.34;
const START_X = 220;
const START_Y = WORLD_HEIGHT * 0.62;
const RESPAWN_GRACE_MS = 1100;
const TREE_COUNT = 14;
const FIREFLY_COUNT = 11;
/**
 * Shared speed cap, keyboard and mouse alike - see the note in update().
 * Raised from the old keyboard-only 347 once capping mouse input to that
 * same number exposed it as too slow for a cursor-chasing light: closing a
 * full 1280px viewport width took ~3.7s and made ordinary repositioning
 * feel sluggish, not just hazard-avoidance fair. 480 keeps a real, equal
 * cap for both inputs (still finite, unlike the old unbounded mouse case)
 * while staying comfortably above every level's hazardSpeed (4x the
 * fastest, level 3's 120) so avoidance is still a real skill, not a freebie.
 */
const WISP_MAX_SPEED = 480;
/**
 * How far a shadow notices the light from. Not a constant any more: a shadow
 * sees the light, so the distance scales with the player's own reach (see
 * alertRadius()). A wisp burning at full reach wakes the glade from a long way
 * off; one that has just spent itself on a pull goes nearly unseen. Noticing
 * is only a look, though - the chase speed ramps in from ALERT_RADIUS_FLOOR,
 * which is fixed. See checkHazardAlerts().
 */
const ALERT_RADIUS_FLOOR = HAZARD_RADIUS * 2.4;
const ALERT_RADIUS_PER_REACH = 0.6;
const ALERT_RADIUS_CEILING = 290;
const ALERT_TIME_SCALE = 1.55;
const ALERT_LIGHT_INTENSITY = 1.55;
const CALM_LIGHT_INTENSITY = 0.9;
const RADIANCE_RADIUS = 390;
const RADIANCE_SLOW_MS = 1800;
const RADIANCE_TIME_SCALE = 0.42;

/**
 * The reach. This round's one verb: your glow is how far you can pull light in,
 * and pulling spends it. Press (click, tap or space) and every mote inside the
 * lit circle comes to you. A pull burns a full glow to its floor, and light
 * carried in by the pull gives back only a glimmer. Walking through five motes
 * rekindles a four-mote reach; until then the press is visibly spent. That makes
 * reaching a choice about which cluster is worth going dark for, rather than a
 * button whose own reward immediately buys another press.
 * The lit radius IS the rule: nothing to read, because you can see exactly as
 * far as you can reach.
 */
const GATHER_COOLDOWN_MS = 420;
/** A reach takes an armful, not a room; the rest stays on the ground. */
const GATHER_MAX_MOTES = 4;
/** Per-mote stagger on the way in - the cascade is the reward, so it lands as notes, not a chord. */
const GATHER_STAGGER_MS = 62;
const GATHER_FLIGHT_MS = 300;
const ECHO_WAKE_RADIUS = 265;
const CURRENT_HALF_WIDTH = 125;
const CURRENT_HALF_HEIGHT = 285;
const CURRENT_PUSH_SPEED = 210;
/** The rebuilt road is calmer water: close to its line, it carries the wisp onward. */
const CAUSEWAY_HALF_WIDTH = 38;
const CAUSEWAY_PUSH_SPEED = 112;

interface EchoStone {
  x: number;
  y: number;
  pushY: -1 | 1;
  awake: boolean;
  core: Phaser.GameObjects.Image;
  ring: Phaser.GameObjects.Graphics;
  light: Phaser.GameObjects.Light;
  current: Phaser.GameObjects.TileSprite;
  /** A different drowned ruin rises with each solved current. */
  monument: Phaser.GameObjects.Graphics;
}

interface LevelInitData {
  levelIndex: number;
  ambience: Ambience;
  resets?: number;
  /** Flawless levels (every mote found) completed earlier in this run. */
  flawless?: number;
  /** The player has already pressed once this run - do not teach the reach again. */
  taught?: boolean;
}

/** Cosmetic per-mood tint - purely a palette shift between stages, same shapes. */
const MOOD_TINT: Record<LevelConfig["mood"], { tree: number[]; ground: number; hillsTint: number }> = {
  dusk: { tree: [0x1b2438, 0x161d2e, 0x141a2a], ground: 0x10151f, hillsTint: 0x0d1526 },
  "deep-night": { tree: [0x141a2c, 0x101624, 0x0e1220], ground: 0x0b0f18, hillsTint: 0x0a0f1e },
  "storm-dark": { tree: [0x171226, 0x120e1e, 0x0f0c1a], ground: 0x0d0a16, hillsTint: 0x120c22 },
  moonwell: { tree: [0x112b3a, 0x0b2230, 0x091a27], ground: 0x061a25, hillsTint: 0x102b3d },
};

/**
 * The reusable stage. One scene, driven entirely by LevelConfig data (see
 * src/levels.ts) - three levels means three configs, not three classes.
 * Everything from BootScene's original slice (Light2D, parallax, the
 * breathing light, ambience) lives here, plus the structure the game was
 * missing after round 1: a real goal (the beacon), a real threat (hazards),
 * and a fail state that costs the player something (this level's progress).
 */
export class LevelScene extends Phaser.Scene {
  private config!: LevelConfig;
  private ambience!: Ambience;
  private resets = 0;
  private flawlessLevels = 0;

  private wisp!: Phaser.GameObjects.Image;
  private wispLight!: Phaser.GameObjects.Light;
  private beacon!: Phaser.GameObjects.Image;
  private beaconLight!: Phaser.GameObjects.Light;
  private trail!: Phaser.GameObjects.Particles.ParticleEmitter;
  private hazardTrail!: Phaser.GameObjects.Particles.ParticleEmitter;

  private moteConfigs: Array<{ x: number; y: number }> = [];
  private motes: Phaser.GameObjects.Image[] = [];
  private hazards: Array<{
    img: Phaser.GameObjects.Image;
    light: Phaser.GameObjects.Light;
    tween?: Phaser.Tweens.Tween;
    alert: boolean;
    /** 0 at the edge of notice, 1 at hunting range - the speed-up ramps across it. */
    pressure: number;
    slowUntil: number;
  }> = [];
  private echoStones: EchoStone[] = [];
  private moonCauseway?: Phaser.GameObjects.Graphics;
  private inCurrent = false;
  private echoHint?: Phaser.GameObjects.Text;
  private echoReminderShown = false;
  /** The Moonwell remembers the last stone changed, so a late hit does not replay solved water. */
  private respawnX = START_X;
  private respawnY = START_Y;

  private hud!: Phaser.GameObjects.Text;
  private levelCard!: Phaser.GameObjects.Text;
  private openLine!: Phaser.GameObjects.Text;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: Record<"up" | "down" | "left" | "right", Phaser.Input.Keyboard.Key>;
  private target = new Phaser.Math.Vector2(START_X, START_Y);
  private chainArc!: Phaser.GameObjects.Graphics;
  private chainText!: Phaser.GameObjects.Text;

  private reachRing!: Phaser.GameObjects.Graphics;
  private reachLine?: Phaser.GameObjects.Text;
  private inviteAt = 0;
  private lastShakeAt = 0;
  private gutter = 0;
  private deathVeil!: Phaser.GameObjects.Rectangle;
  private arrivalVeil!: Phaser.GameObjects.Rectangle;
  private inviteShown = 0;
  private incoming: Phaser.GameObjects.Image[] = [];

  private collected = 0;
  /** Motes actually placed this level - derived from the data used, never assumed from config. */
  private totalMotes = 0;
  private pulseBoost = 0;
  /** How far the light reaches right now - the light's radius, the pull's radius, one number. */
  private reach = REACH_START;
  private gatherReadyAt = 0;
  private gathers = 0;
  private deniedGathers = 0;
  private touchedMotes = 0;
  private gatheredMotes = 0;
  private costShown = false;
  /** Motes were in reach and the player has not pressed yet - drives the wordless invitation. */
  private taught = false;
  private levelClear = false;
  /** Every mote currently collected - the flawless variant is showing. */
  private flawlessNow = false;
  private locked = false;
  private graceUntil = 0;
  private waits: Array<{ remainingMs: number; onComplete: () => void }> = [];
  private chainState: ChainState = emptyChain();

  constructor() {
    super("level");
  }

  init(data: LevelInitData): void {
    this.config = levelFor(data.levelIndex) ?? LEVELS[0];
    this.ambience = data.ambience;
    this.resets = data.resets ?? 0;
    this.flawlessLevels = data.flawless ?? 0;
    this.collected = 0;
    this.totalMotes = 0;
    this.pulseBoost = 0;
    this.reach = REACH_START;
    this.gatherReadyAt = 0;
    this.gathers = 0;
    this.deniedGathers = 0;
    this.touchedMotes = 0;
    this.gatheredMotes = 0;
    this.costShown = false;
    this.inviteAt = 0;
    this.inviteShown = 0;
    this.gutter = 0;
    this.reachLine = undefined;
    this.taught = data.taught ?? false;
    this.levelClear = false;
    this.flawlessNow = false;
    this.locked = false;
    this.waits = [];
    this.chainState = emptyChain();
    this.moteConfigs = [];
    this.motes = [];
    this.incoming = [];
    this.hazards = [];
    this.echoStones = [];
    this.moonCauseway = undefined;
    this.inCurrent = false;
    this.echoHint = undefined;
    this.echoReminderShown = false;
    this.respawnX = START_X;
    this.respawnY = START_Y;
    this.target.set(START_X, START_Y);
  }

  preload(): void {
    makeGlowTexture(this, "wisp", 85, "rgba(255,255,255,1)", "rgba(150,214,255,0.55)");
    makeGlowTexture(this, "mote", 27, "rgba(255,244,214,1)", "rgba(255,196,92,0.5)");
    makeGlowTexture(this, "spark", 16, "rgba(255,255,255,0.9)", "rgba(190,226,255,0.35)");
    makeGlowTexture(this, "firefly", 12, "rgba(226,255,196,1)", "rgba(198,255,130,0.4)");
    makeGlowTexture(this, "beacon", 170, "rgba(255,226,168,1)", "rgba(255,182,102,0.4)");
    makeGlowTexture(this, "echo-stone", 110, "rgba(205,252,255,1)", "rgba(72,216,235,0.48)");
    makeGlowTexture(this, "shadow-spark", 10, "rgba(150,110,220,0.85)", "rgba(90,50,150,0.3)");
    makeHazardTexture(this, `hazard-${this.config.index}`, 30, this.config.index * 97);
    makeSkyTexture(this, "sky", VIEW_WIDTH, VIEW_HEIGHT, 11);
    makeHillsTexture(this, "hills", 1760, 260, 3);
    makeGroundTexture(this, "ground", WORLD_WIDTH, 240, 7);
    for (let i = 0; i < 4; i += 1) {
      makeTreeTexture(this, `tree-${i}`, 240, 560, i + 1);
    }
  }

  create(): void {
    this.lights.enable().setAmbientColor(0x0a0d18);
    this.cameras.main.setBackgroundColor(0x05060c);

    this.buildSky();
    this.buildHills();
    this.buildForest();
    this.buildBeacon();
    this.buildFireflies();
    this.buildMotes();
    this.buildEchoStones();
    this.buildWisp();
    this.buildHazards();
    this.buildStorm();
    this.buildCamera();
    this.buildVignette();
    this.buildHud();
    this.bindInput();

    this.ambience.setStorm(this.config.mood === "storm-dark");

    this.graceUntil = this.time.now + RESPAWN_GRACE_MS;
    this.cameras.main.fadeIn(420, 5, 6, 12);
    this.events.once(Phaser.Scenes.Events.POST_UPDATE, () => this.announceReady());
  }

  private buildSky(): void {
    this.add.image(VIEW_WIDTH / 2, VIEW_HEIGHT / 2, "sky").setScrollFactor(0).setDepth(-100);
  }

  private buildHills(): void {
    const tint = MOOD_TINT[this.config.mood].hillsTint;
    this.add.image(0, WORLD_HEIGHT - 150, "hills").setOrigin(0, 1).setTint(tint).setScrollFactor(0.25).setDepth(-40);
  }

  private buildForest(): void {
    if (this.config.mood === "moonwell") {
      this.buildMoonwell();
      return;
    }
    const rng = new Phaser.Math.RandomDataGenerator([`start-of-glow-trees-${this.config.index}`]);
    const tints = MOOD_TINT[this.config.mood].tree;
    for (let i = 0; i < TREE_COUNT; i += 1) {
      const x = 60 + (i / (TREE_COUNT - 1)) * (WORLD_WIDTH - 120) + rng.between(-45, 45);
      const tree = this.add
        .image(x, WORLD_HEIGHT - 120 + rng.between(-8, 8), `tree-${i % 4}`)
        .setOrigin(0.5, 1)
        .setScale(rng.realInRange(0.75, 1.3))
        .setTint(tints[rng.between(0, tints.length - 1)])
        .setDepth(-30);
      tree.setPipeline("Light2D");
    }

    const ground = this.add
      .image(0, WORLD_HEIGHT, "ground")
      .setOrigin(0, 1)
      .setTint(MOOD_TINT[this.config.mood].ground)
      .setDepth(-10);
    ground.setPipeline("Light2D");
  }

  /**
   * The first place beyond the forest: a continuous flooded basin with a pale
   * horizon, moon reflections, reed banks and no tree silhouettes. It uses
   * primitive/vector texture work so the zone stays crisp and deterministic.
   */
  private buildMoonwell(): void {
    const water = this.add
      .rectangle(WORLD_WIDTH / 2, WORLD_HEIGHT * 0.61, WORLD_WIDTH, WORLD_HEIGHT * 0.78, 0x071b29)
      .setDepth(-32);
    water.setPipeline("Light2D");

    const horizon = this.add.graphics().setDepth(-33);
    horizon.fillStyle(0x16394b, 0.72);
    horizon.fillEllipse(WORLD_WIDTH / 2, WORLD_HEIGHT * 0.61, WORLD_WIDTH * 1.25, 360);
    horizon.fillStyle(0x8bd7dd, 0.08);
    horizon.fillEllipse(WORLD_WIDTH * 0.78, WORLD_HEIGHT * 0.28, 430, 90);

    // The open basin owns one landmark the forest never had: a pale moon and
    // its broken reflection, visible across the entire act. The three drowned
    // monuments below answer that same vertical shape as the player wakes them.
    horizon.fillStyle(0xb9f5f1, 0.2);
    horizon.fillCircle(WORLD_WIDTH * 0.68, 118, 72);
    horizon.fillStyle(0xe0ffff, 0.08);
    horizon.fillEllipse(WORLD_WIDTH * 0.68, 360, 54, 410);

    const ripples = this.add.graphics().setDepth(-14);
    for (let x = 40; x < WORLD_WIDTH; x += 150) {
      const y = 170 + ((x * 37) % 430);
      const width = 34 + ((x * 13) % 58);
      ripples.lineStyle(2, x % 300 === 40 ? 0x8edbe2 : 0x3b8798, 0.18);
      ripples.strokeEllipse(x, y, width, 8);
    }

    const banks = this.add.graphics().setDepth(-12);
    banks.fillStyle(0x07131c, 1);
    banks.fillRect(0, 0, WORLD_WIDTH, 75);
    banks.fillRect(0, WORLD_HEIGHT - 58, WORLD_WIDTH, 58);
    banks.lineStyle(5, 0x2c6571, 0.42);
    banks.lineBetween(0, 76, WORLD_WIDTH, 76);
    banks.lineBetween(0, WORLD_HEIGHT - 60, WORLD_WIDTH, WORLD_HEIGHT - 60);
    for (let x = 40; x < WORLD_WIDTH; x += 88) {
      const topHeight = 26 + ((x * 17) % 48);
      banks.lineStyle(4, 0x163d46, 0.75);
      banks.lineBetween(x, 76, x - 8, 76 - topHeight);
      banks.lineBetween(x + 28, WORLD_HEIGHT - 60, x + 38, WORLD_HEIGHT - 60 + topHeight);
    }
  }

  /** Three permanent world switches and their visible cross-currents. */
  private buildEchoStones(): void {
    if (!this.config.echoStones?.length) return;
    this.moonCauseway = this.add.graphics().setDepth(-2).setAlpha(0.82);
    const currentKey = "moon-current";
    if (!this.textures.exists(currentKey)) {
      const texture = this.textures.createCanvas(currentKey, 128, 128)!;
      const ctx = texture.getContext();
      ctx.clearRect(0, 0, 128, 128);
      ctx.strokeStyle = "rgba(116,224,235,0.3)";
      ctx.lineWidth = 2;
      for (let y = 12; y < 128; y += 24) {
        ctx.beginPath();
        ctx.moveTo(4, y);
        ctx.bezierCurveTo(34, y - 9, 88, y + 9, 124, y);
        ctx.stroke();
      }
      texture.refresh();
    }

    for (const [index, cfg] of this.config.echoStones.entries()) {
      const current = this.add
        .tileSprite(cfg.x, cfg.y, CURRENT_HALF_WIDTH * 2, CURRENT_HALF_HEIGHT * 2, currentKey)
        .setTint(cfg.pushY > 0 ? 0x75d5e2 : 0x9fe8dd)
        .setAlpha(0.42)
        .setDepth(-4);
      const ring = this.add.graphics().setDepth(3);
      ring.lineStyle(3, 0x6ccbd8, 0.55);
      ring.strokeCircle(cfg.x, cfg.y, 56);
      ring.lineStyle(1, 0xb7f8ff, 0.22);
      ring.strokeCircle(cfg.x, cfg.y, ECHO_WAKE_RADIUS);
      const core = this.add
        .image(cfg.x, cfg.y, "echo-stone")
        .setBlendMode(Phaser.BlendModes.ADD)
        .setTint(0x6c9aa8)
        .setAlpha(0.32)
        .setScale(0.62)
        .setDepth(4);
      const light = this.lights.addLight(cfg.x, cfg.y, 115, 0x6ad9e8, 0.28);
      const monument = this.buildMoonMonument(index, cfg.x, cfg.y);
      this.echoStones.push({ ...cfg, awake: false, core, ring, light, current, monument });
      this.tweens.add({
        targets: core,
        alpha: { from: 0.22, to: 0.42 },
        scale: { from: 0.58, to: 0.66 },
        duration: 1250,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
    }
  }

  /**
   * Three silhouettes, not three copies: an arch, an obelisk, and twin fins.
   * They begin mostly below the waterline. Spending reach raises the matching
   * ruin, so a solved switch remains legible even after its ring leaves camera.
   */
  private buildMoonMonument(index: number, x: number, y: number): Phaser.GameObjects.Graphics {
    const monument = this.add.graphics().setPosition(x, y + 142).setDepth(-1).setAlpha(0.18);
    monument.fillStyle(0x163e4c, 0.92);
    monument.lineStyle(6, 0x8ce4e7, 0.72);

    if (index === 0) {
      monument.fillStyle(0x5cc6d3, 0.42);
      monument.fillCircle(0, -112, 78);
      monument.fillStyle(0x071b29, 0.96);
      monument.fillCircle(0, -112, 57);
      monument.fillStyle(0x163e4c, 0.92);
      monument.fillRect(-78, -118, 24, 150);
      monument.fillRect(54, -118, 24, 150);
      monument.strokeCircle(0, -112, 67);
      monument.lineBetween(-78, -112, -54, -112);
      monument.lineBetween(54, -112, 78, -112);
    } else if (index === 1) {
      monument.fillPoints([
        new Phaser.Geom.Point(-46, 32),
        new Phaser.Geom.Point(-28, -136),
        new Phaser.Geom.Point(0, -205),
        new Phaser.Geom.Point(28, -136),
        new Phaser.Geom.Point(46, 32),
      ], true);
      monument.strokePoints([
        new Phaser.Geom.Point(-46, 32),
        new Phaser.Geom.Point(-28, -136),
        new Phaser.Geom.Point(0, -205),
        new Phaser.Geom.Point(28, -136),
        new Phaser.Geom.Point(46, 32),
      ], true);
      monument.lineBetween(-20, -92, 20, -92);
    } else {
      monument.fillPoints([
        new Phaser.Geom.Point(-94, 32),
        new Phaser.Geom.Point(-76, -132),
        new Phaser.Geom.Point(-28, -54),
        new Phaser.Geom.Point(-18, 32),
      ], true);
      monument.fillPoints([
        new Phaser.Geom.Point(18, 32),
        new Phaser.Geom.Point(28, -54),
        new Phaser.Geom.Point(76, -132),
        new Phaser.Geom.Point(94, 32),
      ], true);
      monument.lineBetween(-94, 32, -76, -132);
      monument.lineBetween(-76, -132, -28, -54);
      monument.lineBetween(94, 32, 76, -132);
      monument.lineBetween(76, -132, 28, -54);
    }

    monument.fillStyle(0x67cfda, 0.65);
    monument.fillEllipse(0, 30, index === 1 ? 118 : 210, 18);
    return monument;
  }

  /** The raised ruins join into a persistent lit road toward the next objective. */
  private redrawMoonCauseway(): void {
    if (!this.moonCauseway) return;
    this.moonCauseway.clear();
    const nodes = [
      ...this.echoStones.map((stone) => ({ x: stone.x, y: stone.y })),
      { x: BEACON_X, y: BEACON_Y },
    ];
    for (let index = 0; index < this.echoStones.length; index += 1) {
      if (!this.echoStones[index].awake) continue;
      const from = nodes[index];
      const to = nodes[index + 1];
      this.moonCauseway.lineStyle(18, 0x2b9aaa, 0.11);
      this.moonCauseway.lineBetween(from.x, from.y, to.x, to.y);
      this.moonCauseway.lineStyle(3, 0xbaf9f5, 0.7);
      this.moonCauseway.lineBetween(from.x, from.y, to.x, to.y);
      for (let step = 1; step <= 4; step += 1) {
        const t = step / 5;
        this.moonCauseway.fillStyle(0xd8ffff, 0.62);
        this.moonCauseway.fillCircle(
          Phaser.Math.Linear(from.x, to.x, t),
          Phaser.Math.Linear(from.y, to.y, t),
          4,
        );
      }
    }
  }

  /** Dark until every mote in the level is found - then it lights, and pulls the player in for the arrival. */
  private buildBeacon(): void {
    this.beacon = this.add.image(BEACON_X, BEACON_Y, "beacon").setBlendMode(Phaser.BlendModes.ADD).setDepth(-35).setAlpha(0.05);
    this.beaconLight = this.lights.addLight(BEACON_X, BEACON_Y, 260, 0xffcf8a, 0);
  }

  private buildFireflies(): void {
    const rng = new Phaser.Math.RandomDataGenerator([`start-of-glow-fireflies-${this.config.index}`]);
    for (let i = 0; i < FIREFLY_COUNT; i += 1) {
      const startX = rng.between(60, WORLD_WIDTH - 60);
      const startY = rng.between(180, WORLD_HEIGHT - 100);
      const firefly = this.add
        .image(startX, startY, "firefly")
        .setBlendMode(Phaser.BlendModes.ADD)
        .setScrollFactor(0.75)
        .setScale(rng.realInRange(0.5, 1))
        .setAlpha(rng.realInRange(0.35, 0.8))
        .setDepth(-5);

      this.tweens.add({
        targets: firefly,
        x: startX + rng.between(-70, 70),
        y: startY + rng.between(-50, 50),
        duration: rng.between(3600, 6200),
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
      this.tweens.add({
        targets: firefly,
        alpha: { from: firefly.alpha * 0.4, to: firefly.alpha },
        duration: rng.between(900, 1700),
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
        delay: rng.between(0, 800),
      });
    }
  }

  private buildMotes(): void {
    if (this.config.layout) {
      this.moteConfigs = this.config.layout.motes.map((m) => ({ ...m }));
    } else {
      const rng = new Phaser.Math.RandomDataGenerator([`start-of-glow-${this.config.index}`]);
      const near = Math.ceil(this.config.moteCount / 2);
      for (let i = 0; i < this.config.moteCount; i += 1) {
        const x = i < near ? rng.between(80, VIEW_WIDTH - 80) : rng.between(VIEW_WIDTH + 40, WORLD_WIDTH - 80);
        this.moteConfigs.push({ x, y: rng.between(140, WORLD_HEIGHT - 160) });
      }
    }
    this.totalMotes = this.moteConfigs.length;
    this.spawnMotes();
  }

  private spawnMotes(): void {
    for (const m of this.motes.concat(this.incoming)) {
      this.tweens.killTweensOf(m);
      m.destroy();
    }
    this.motes = [];
    this.incoming = [];
    const rng = new Phaser.Math.RandomDataGenerator([`start-of-glow-motes-${this.config.index}`]);
    for (const cfg of this.moteConfigs) {
      const mote = this.add.image(cfg.x, cfg.y, "mote").setBlendMode(Phaser.BlendModes.ADD).setScale(0.55).setDepth(5);
      this.tweens.add({
        targets: mote,
        y: cfg.y - rng.between(8, 21),
        alpha: { from: 0.55, to: 1 },
        duration: rng.between(1200, 2200),
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
      this.motes.push(mote);
    }
  }

  private buildWisp(): void {
    this.trail = this.add.particles(0, 0, "spark", {
      speed: { min: 6, max: 30 },
      lifespan: { min: 500, max: 1100 },
      scale: { start: 0.6, end: 0 },
      alpha: { start: 0.55, end: 0 },
      tint: [0xffffff, 0x9fd8ff, 0xffe6a8],
      blendMode: Phaser.BlendModes.ADD,
      frequency: 40,
      quantity: 1,
      emitZone: { type: "random", source: new Phaser.Geom.Circle(0, 0, 19), quantity: 1 },
    });
    this.trail.setDepth(9);

    this.wisp = this.add.image(this.target.x, this.target.y, "wisp").setBlendMode(Phaser.BlendModes.ADD).setScale(0.5).setDepth(10);
    this.wispLight = this.lights.addLight(this.wisp.x, this.wisp.y, REACH_START, 0xbfe4ff, 1.6);
    this.trail.startFollow(this.wisp);

    // The edge of the light, drawn thin. Light2D already falls off at exactly
    // this radius, but a soft gradient does not tell you where the rule ends -
    // this does, and it only becomes bright when there is something to take.
    this.reachRing = this.add.graphics().setDepth(4);
    // One source of truth for the wisp's size: derive the spawn scale from the
    // starting reach rather than leaving a hand-set 0.5 that the first collect
    // would silently correct.
    this.setReach(REACH_START);
  }

  /**
   * Shadow-wisps: the thing the light is not. Each patrols a small loop of
   * waypoints (deterministic per level+index) at the level's hazardSpeed.
   * Touching one snuffs the player's light and resets the level's progress -
   * see fail(). They carry a dim cold light of their own, not because a real
   * shadow would, but because a threat the player cannot see coming in a
   * game about darkness is cheap, not hard.
   */
  private buildHazards(): void {
    const trailEmitter = this.add.particles(0, 0, "shadow-spark", {
      speed: { min: 4, max: 16 },
      lifespan: { min: 300, max: 650 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 0.5, end: 0 },
      blendMode: Phaser.BlendModes.ADD,
      frequency: 70,
      quantity: 1,
    });
    trailEmitter.setDepth(8);
    this.hazardTrail = trailEmitter;

    const rng = new Phaser.Math.RandomDataGenerator([`start-of-glow-hazards-${this.config.index}`]);
    const count = this.config.layout ? this.config.layout.hazards.length : this.config.hazardCount;
    for (let i = 0; i < count; i += 1) {
      const img = this.add
        .image(0, 0, `hazard-${this.config.index}`)
        .setDepth(6)
        .setScale(rng.realInRange(0.85, 1.15));
      const light = this.lights.addLight(0, 0, 130, 0x9a6efa, CALM_LIGHT_INTENSITY);
      const hazard = { img, light, alert: false, pressure: 0, slowUntil: 0 };
      this.hazards.push(hazard);

      const waypoints: Phaser.Math.Vector2[] = [];
      if (this.config.layout) {
        for (const w of this.config.layout.hazards[i]) {
          waypoints.push(new Phaser.Math.Vector2(w.x, w.y));
        }
      } else {
        const legs = 3;
        for (let w = 0; w < legs; w += 1) {
          waypoints.push(
            new Phaser.Math.Vector2(rng.between(340, WORLD_WIDTH - 100), rng.between(120, WORLD_HEIGHT - 140)),
          );
        }
      }
      img.setPosition(waypoints[0].x, waypoints[0].y);
      light.setPosition(waypoints[0].x, waypoints[0].y);
      this.patrol(hazard, waypoints, 0);
    }
  }

  /**
   * The storm-dark weather layer - level 3's identity beyond a palette shift.
   * Wind-blown flecks drift left across the near field, and a seeded flicker
   * schedule fires distant lightning behind the hills (a screen-space wash
   * above the sky, below everything else) with a soft thunder swell. Fully
   * deterministic per run, like every other moving part in a level.
   */
  private buildStorm(): void {
    if (this.config.mood !== "storm-dark") return;

    const flecks = this.add.particles(0, 0, "spark", {
      x: { min: -120, max: VIEW_WIDTH + 260 },
      y: { min: -60, max: VIEW_HEIGHT },
      speedX: { min: -150, max: -80 },
      speedY: { min: 18, max: 42 },
      lifespan: { min: 1300, max: 2400 },
      scale: { start: 0.3, end: 0.08 },
      alpha: { start: 0.3, end: 0 },
      quantity: 1,
      frequency: 55,
      tint: [0x8a9fd8, 0x6f83c4, 0xa9b8e8],
      blendMode: Phaser.BlendModes.ADD,
    });
    flecks.setScrollFactor(0.9).setDepth(-8);

    const rng = new Phaser.Math.RandomDataGenerator([`start-of-glow-storm-${this.config.index}`]);
    const flash = this.add
      .rectangle(VIEW_WIDTH / 2, VIEW_HEIGHT / 2, VIEW_WIDTH, VIEW_HEIGHT, 0xcdd8ff)
      .setScrollFactor(0)
      .setAlpha(0)
      .setDepth(-90);
    const schedule = (): void => {
      this.after(rng.between(6500, 12500), () => {
        if (!flash.active) return;
        this.ambience.rumble();
        this.tweens.add({
          targets: flash,
          alpha: { from: 0, to: 0.07 },
          duration: 90,
          yoyo: true,
          repeat: 1,
          repeatDelay: 60,
          onComplete: () => schedule(),
        });
      });
    };
    schedule();
  }

  /**
   * Noticing and hunting are two different distances. A shadow *sees* the light
   * as far as the light carries (alertRadius, which grows with the reach), and
   * that is a look: its own glow comes up so the player can read it from across
   * the glade. It only *hunts* at close quarters, and the speed-up ramps in
   * between. Coupling the full chase speed to the reach - the first version of
   * this - punished the one player who most needs help, the one who has not
   * found the press yet and is therefore walking around at full brightness.
   *
   * Either way it is the running patrol tween's timeScale that changes, never
   * the path - the patrol shape stays exactly as authored, so a hazard is
   * reactive without ever feeling like it cheated.
   */
  private checkHazardAlerts(): void {
    const notice = this.alertRadius();
    for (const h of this.hazards) {
      const dist = Phaser.Math.Distance.Between(h.img.x, h.img.y, this.wisp.x, this.wisp.y);
      h.alert = dist <= notice;
      h.pressure = h.alert
        ? Phaser.Math.Clamp(1 - (dist - ALERT_RADIUS_FLOOR) / Math.max(1, notice - ALERT_RADIUS_FLOOR), 0, 1)
        : 0;
      if (h.tween) h.tween.timeScale = this.hazardTimeScale(h);
      h.light.intensity = h.slowUntil > this.time.now
        ? 0.62
        : CALM_LIGHT_INTENSITY + (ALERT_LIGHT_INTENSITY - CALM_LIGHT_INTENSITY) * (h.alert ? 0.4 + 0.6 * h.pressure : 0);
    }
  }

  /** The distance at which a shadow notices the light - as far as the light carries. */
  private alertRadius(): number {
    return Phaser.Math.Clamp(this.reach * ALERT_RADIUS_PER_REACH, ALERT_RADIUS_FLOOR, ALERT_RADIUS_CEILING);
  }

  private hazardTimeScale(hazard: { alert: boolean; pressure: number; slowUntil: number }): number {
    if (hazard.slowUntil > this.time.now) return RADIANCE_TIME_SCALE;
    return 1 + (ALERT_TIME_SCALE - 1) * hazard.pressure;
  }

  private patrol(
    hazard: { img: Phaser.GameObjects.Image; light: Phaser.GameObjects.Light; tween?: Phaser.Tweens.Tween; alert: boolean; pressure: number; slowUntil: number },
    waypoints: Phaser.Math.Vector2[],
    index: number,
  ): void {
    const { img, light } = hazard;
    const next = waypoints[(index + 1) % waypoints.length];
    const dist = Phaser.Math.Distance.Between(img.x, img.y, next.x, next.y);
    const duration = (dist / this.config.hazardSpeed) * 1000;
    hazard.tween = this.tweens.add({
      targets: img,
      x: next.x,
      y: next.y,
      duration,
      ease: "Sine.easeInOut",
      onUpdate: () => light.setPosition(img.x, img.y),
      onComplete: () => {
        if (!img.active) return;
        this.patrol(hazard, waypoints, index + 1);
      },
    });
    hazard.tween.timeScale = this.hazardTimeScale(hazard);
  }

  private buildCamera(): void {
    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cameras.main.startFollow(this.wisp, false, 0.09, 0.09);
  }

  private buildVignette(): void {
    const width = VIEW_WIDTH;
    const height = VIEW_HEIGHT;
    const key = "vignette";
    if (!this.textures.exists(key)) {
      const texture = this.textures.createCanvas(key, width, height);
      const ctx = texture!.getContext();
      const cx = width / 2;
      const cy = height / 2;
      const gradient = ctx.createRadialGradient(cx, cy, Math.min(width, height) * 0.32, cx, cy, Math.max(width, height) * 0.72);
      gradient.addColorStop(0, "rgba(0,0,0,0)");
      gradient.addColorStop(1, "rgba(0,0,0,0.78)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      texture!.refresh();
    }
    this.add.image(width / 2, height / 2, key).setScrollFactor(0).setDepth(90);

    this.deathVeil = this.add
      .rectangle(width / 2, height / 2, width, height, 0x120424)
      .setScrollFactor(0)
      .setAlpha(0)
      .setDepth(94);

    this.arrivalVeil = this.add
      .rectangle(width / 2, height / 2, width, height, 0xffe8c0)
      .setScrollFactor(0)
      .setAlpha(0)
      .setDepth(93)
      .setBlendMode(Phaser.BlendModes.ADD);
  }

  /**
   * Teaching the press without saying it: a ghost of the reach collapsing
   * inward, repeated every couple of seconds while there is something in range
   * and the player has not tried it yet. It is the gather's own animation
   * played at a whisper - the first real press then looks like the answer to
   * a question the screen already asked. It stops for good on that press.
   */
  private inviteGather(): void {
    // Six is asking; more than six is nagging. After that the quiet line on
    // level 1 is the only thing still offering, and the screen goes back to
    // being the player's problem.
    if (this.taught || this.locked || this.inviteShown >= 6 || this.inviteAt > this.time.now) return;
    let inReach = false;
    for (const mote of this.motes) {
      if (Phaser.Math.Distance.Between(mote.x, mote.y, this.wisp.x, this.wisp.y) <= this.reach) {
        inReach = true;
        break;
      }
    }
    if (!inReach) return;
    this.inviteAt = this.time.now + 2100;
    const ghost = this.add
      .circle(this.wisp.x, this.wisp.y, this.reach, 0xffe2a8, 0)
      .setStrokeStyle(2, 0xffe2a8, 0.3)
      .setDepth(4);
    this.tweens.add({
      targets: ghost,
      radius: 18,
      alpha: 0,
      duration: 900,
      ease: "Cubic.easeIn",
      onUpdate: () => ghost.setPosition(this.wisp.x, this.wisp.y),
      onComplete: () => ghost.destroy(),
    });
    // Words are the fallback, not the lesson: only after the wordless version
    // has played three times unanswered, and only on the first level.
    this.inviteShown += 1;
    if (this.inviteShown === 3 && this.config.index === 1 && this.reachLine) {
      this.tweens.add({ targets: this.reachLine, alpha: { from: 0, to: 0.75 }, duration: 700, ease: "Sine.easeInOut" });
    }
  }

  private buildHud(): void {
    this.hud = this.add
      .text(27, 24, "", {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "17px",
        color: "#7e93b8",
      })
      .setAlpha(0.85)
      .setDepth(100)
      .setScrollFactor(0);

    this.levelCard = this.add
      .text(VIEW_WIDTH / 2, 46, `${this.config.index} · ${this.config.name}`, {
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: "20px",
        color: "#e7dcc2",
      })
      .setOrigin(0.5, 0)
      .setAlpha(0)
      .setDepth(100)
      .setScrollFactor(0);
    this.tweens.add({
      targets: this.levelCard,
      alpha: { from: 0, to: 0.9 },
      duration: 900,
      yoyo: true,
      hold: 1400,
      ease: "Sine.easeInOut",
    });

    this.openLine = this.add
      .text(VIEW_WIDTH / 2, 80, "the beacon is lit", {
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: "17px",
        color: "#ffd9a0",
      })
      .setOrigin(0.5, 0)
      .setAlpha(0)
      .setDepth(100)
      .setScrollFactor(0);

    if (this.config.index === 1) {
      this.reachLine = this.add
        .text(VIEW_WIDTH / 2, VIEW_HEIGHT - 54, "press · draw the light in", {
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: "17px",
          color: "#ffd9a0",
        })
        .setOrigin(0.5)
        .setAlpha(0)
        .setDepth(100)
        .setScrollFactor(0);
    }

    if (this.config.mood === "moonwell") {
      this.echoHint = this.add
        .text(VIEW_WIDTH / 2, VIEW_HEIGHT - 54, "press near each moonstone · spend the reach", {
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: "17px",
          color: "#bdf8ff",
        })
        .setOrigin(0.5)
        .setAlpha(0.82)
        .setDepth(100)
        .setScrollFactor(0);
      this.tweens.add({
        targets: this.echoHint,
        alpha: 0,
        duration: 700,
        delay: 3500,
        ease: "Sine.easeInOut",
      });
    }

    this.chainArc = this.add.graphics().setDepth(95).setScrollFactor(0);
    this.chainText = this.add.text(VIEW_WIDTH - 28, 24, "", {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: "15px",
      color: "#ffd9a0",
      letterSpacing: 2,
    }).setOrigin(1, 0).setDepth(100).setScrollFactor(0);

    this.updateHud();
  }

  private bindInput(): void {
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (pointer: Phaser.Input.Pointer) => {
      if (this.locked) return;
      this.target.set(pointer.worldX, pointer.worldY);
    });
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      this.ambience.unlock();
      if (this.locked) return;
      this.target.set(pointer.worldX, pointer.worldY);
      this.gather();
    });
    this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE).on("down", () => {
      this.ambience.unlock();
      this.gather();
    });
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasd = {
      up: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      left: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };
  }

  /**
   * The press. Everything inside the lit circle is drawn in, nearest first, and
   * the circle pays for it. Only a fully kindled glow can reach. The press burns
   * that glow to its floor whether or not it catches anything; moving through
   * light, rather than pulling it, is what earns the next reach.
   */
  private gather(): void {
    if (this.locked || this.time.now < this.gatherReadyAt) return;
    this.gatherReadyAt = this.time.now + GATHER_COOLDOWN_MS;
    // Unlike pulling loose light, a moonstone will accept any reach that has
    // begun to rekindle. That makes the late-act choice "spend what I have to
    // still the current" rather than forcing a hidden exact-full threshold.
    const echo = this.reach >= REACH_MIN + 24
      ? this.echoStones.find(
        (stone) => !stone.awake && Phaser.Math.Distance.Between(stone.x, stone.y, this.wisp.x, this.wisp.y) <= ECHO_WAKE_RADIUS,
      )
      : undefined;
    if (!reachReady(this.reach) && !echo) {
      this.deniedGathers += 1;
      this.gatherDenied();
      this.reportState();
      return;
    }
    this.gathers += 1;
    if (!this.taught) {
      this.taught = true;
      if (this.reachLine) {
        this.tweens.killTweensOf(this.reachLine);
        this.reachLine.setAlpha(0);
      }
    }

    const caught: Array<{ mote: Phaser.GameObjects.Image; d: number }> = [];
    // A moonstone takes the whole reach into the world. It does not also pull
    // nearby motes: leaving those lights in place gives the player the visible
    // route that rekindles the next world-changing spend.
    if (!echo) {
      for (let i = this.motes.length - 1; i >= 0; i -= 1) {
        const mote = this.motes[i];
        const d = Phaser.Math.Distance.Between(mote.x, mote.y, this.wisp.x, this.wisp.y);
        if (d > this.reach) continue;
        this.motes.splice(i, 1);
        this.incoming.push(mote);
        caught.push({ mote, d });
      }
    }
    caught.sort((a, b) => a.d - b.d);
    // A reach takes an armful, not a room. The overflow goes straight back so
    // a wide-open press still leaves something to walk to, and the cascade
    // stays a phrase you can hear rather than a chord.
    for (const spare of caught.splice(GATHER_MAX_MOTES)) {
      this.incoming.splice(this.incoming.indexOf(spare.mote), 1);
      this.motes.push(spare.mote);
    }

    const spent = this.reach;
    this.setReach(spendReach(this.reach));
    if (echo) this.wakeEcho(echo);
    this.gatherWave(spent, caught.length);
    this.ambience.gather(caught.length);
    this.pulseBoost = caught.length > 0 ? 1.5 : 0.5;
    this.showGatherCost();

    caught.forEach(({ mote, d }, index) => {
      this.tweens.killTweensOf(mote);
      this.tweens.add({
        targets: mote,
        x: () => this.wisp.x,
        y: () => this.wisp.y,
        scale: 0.9,
        alpha: 1,
        delay: index * GATHER_STAGGER_MS,
        duration: GATHER_FLIGHT_MS + d * 0.28,
        ease: "Cubic.easeIn",
        onComplete: () => this.absorb(mote),
      });
    });
  }

  /** Spending reach here changes the level permanently: one current goes still. */
  private wakeEcho(stone: EchoStone): void {
    stone.awake = true;
    // The world switch is also a spatial promise: once the player has spent
    // their light to still this water, a later shadow cannot make them cross
    // that solved current again. Land just beyond the stone, toward the beacon.
    this.respawnX = Phaser.Math.Clamp(stone.x + 105, 27, WORLD_WIDTH - 27);
    this.respawnY = stone.y;
    this.washAwayNearestShadow(stone);
    this.tweens.killTweensOf(stone.core);
    stone.core.setTint(0xc8fbff).setAlpha(0.95);
    stone.light.intensity = 2.1;
    stone.light.radius = 310;
    this.tweens.add({ targets: stone.current, alpha: 0, duration: 760, ease: "Sine.easeOut" });
    this.tweens.add({
      targets: stone.monument,
      y: stone.y + 12,
      alpha: 0.9,
      duration: 980,
      ease: "Cubic.easeOut",
    });
    this.redrawMoonCauseway();
    this.tweens.add({
      targets: stone.core,
      scale: { from: stone.core.scale, to: 1.18 },
      duration: 380,
      yoyo: true,
      ease: "Cubic.easeOut",
    });
    // The broad dormant interaction radius has done its teaching. Remove it
    // completely once solved; leaving that 530px circle beside the player's
    // reach and alert rings made the transformed basin read as visual noise.
    stone.ring.setVisible(false);
    this.add.circle(stone.x, stone.y, 62, 0xc7fbff, 0)
      .setStrokeStyle(5, 0xc7fbff, 0.86)
      .setDepth(3)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.ambience.echoAwake(this.echoesAwake());
    this.cameras.main.shake(220, 0.0022);
    if (this.echoHint) {
      const remaining = this.echoStones.length - this.echoesAwake();
      this.tweens.killTweensOf(this.echoHint);
      this.echoHint.setText(
        remaining > 0
          ? `the moonwell remembers · shadow washed away · ${remaining} current${remaining === 1 ? "" : "s"} running`
          : "the water is still · no shadows remain",
      );
      this.echoHint.setAlpha(0.9);
      this.tweens.add({ targets: this.echoHint, alpha: 0, duration: 1300, delay: 950, ease: "Sine.easeIn" });
      // After the satisfying transient, restore the objective if the player
      // already brought enough loose light but still has another stone to use.
      this.after(2350, () => {
        if (!this.echoHint || this.echoesAwake() >= this.echoStones.length || this.collected < this.requiredMotes()) return;
        this.echoHint.setText("the beacon waits · press inside each pale ring").setAlpha(0.92);
      });
    }
    this.grow();
    this.updateHud();
    this.reportState();
  }

  /**
   * Each stone changes the whole local problem, not only its current. The
   * basin-wide wake ring catches the nearest shadow and dissolves it into the
   * same pale sparks as the player, leaving one less patrol to cross forever.
   */
  private washAwayNearestShadow(stone: EchoStone): void {
    if (this.hazards.length === 0) return;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    this.hazards.forEach((hazard, index) => {
      const distance = Phaser.Math.Distance.Between(stone.x, stone.y, hazard.img.x, hazard.img.y);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    const [hazard] = this.hazards.splice(nearestIndex, 1);
    hazard.tween?.stop();
    hazard.img.setTint(0x9df3f4);
    hazard.light.setColor(0x8eeaf2).setIntensity(1.8);
    this.trail.explode(28, hazard.img.x, hazard.img.y);
    this.tweens.add({
      targets: hazard.img,
      scale: 0.12,
      alpha: 0,
      duration: 720,
      ease: "Cubic.easeOut",
      onUpdate: () => hazard.light.setPosition(hazard.img.x, hazard.img.y).setIntensity(hazard.img.alpha * 1.8),
      onComplete: () => {
        this.lights.removeLight(hazard.light);
        hazard.img.destroy();
      },
    });
  }

  private echoesAwake(): number {
    return this.echoStones.filter((stone) => stone.awake).length;
  }

  /** A spent press answers immediately, but cannot pull or hide its state. */
  private gatherDenied(): void {
    this.ambience.gather(0);
    const ring = this.add
      .circle(this.wisp.x, this.wisp.y, 42, 0x8fb4d8, 0)
      .setStrokeStyle(2, 0x8fb4d8, 0.7)
      .setDepth(8);
    this.tweens.add({
      targets: ring,
      radius: 22,
      alpha: 0,
      duration: 260,
      ease: "Cubic.easeOut",
      onUpdate: () => ring.setPosition(this.wisp.x, this.wisp.y),
      onComplete: () => ring.destroy(),
    });
  }

  /** One quiet sentence, once, after the screen has already shown the cost. */
  private showGatherCost(): void {
    if (this.costShown || this.config.index !== 1 || !this.reachLine) return;
    this.costShown = true;
    this.reachLine.setText("move through light to kindle the reach").setAlpha(0);
    this.tweens.add({ targets: this.reachLine, alpha: 0.72, duration: 260, ease: "Sine.easeOut" });
    this.after(1750, () => {
      if (!this.reachLine) return;
      this.tweens.add({ targets: this.reachLine, alpha: 0, duration: 520, ease: "Sine.easeIn" });
    });
  }

  /** The inward ring: the reach collapsing onto the wisp, so the press has a shape. */
  private gatherWave(from: number, caughtCount: number): void {
    const hit = caughtCount > 0;
    const ring = this.add
      .circle(this.wisp.x, this.wisp.y, from, 0xffe2a8, 0)
      .setStrokeStyle(hit ? 3 : 1.5, hit ? 0xffe2a8 : 0x8fb4d8, hit ? 0.8 : 0.34)
      .setDepth(7);
    this.tweens.add({
      targets: ring,
      radius: 14,
      alpha: hit ? 0.9 : 0.25,
      duration: hit ? 300 : 230,
      ease: "Cubic.easeIn",
      onUpdate: () => ring.setPosition(this.wisp.x, this.wisp.y),
      onComplete: () => ring.destroy(),
    });
    if (hit) this.cameras.main.shake(70, 0.0012);
  }

  /** A gathered mote reaching the wisp - the same collect as a touch, one flight later. */
  private absorb(mote: Phaser.GameObjects.Image): void {
    const index = this.incoming.indexOf(mote);
    if (index >= 0) this.incoming.splice(index, 1);
    mote.destroy();
    // A shadow caught the wisp while this one was still in flight: the light
    // that snuffed the run does not get to bank the mote that was on its way.
    if (this.locked) return;
    this.trail.explode(16, this.wisp.x, this.wisp.y);
    this.takeMote("gathered");
  }

  private setReach(next: number): void {
    this.reach = Phaser.Math.Clamp(next, REACH_MIN, REACH_MAX);
    this.wispLight.radius = this.reach;
    this.wisp.setScale(0.34 + (this.reach / REACH_MAX) * 0.42);
  }

  /** Draw the edge of the reach, bright only while it has something in it. */
  private drawReachRing(time: number): void {
    this.reachRing.clear();
    if (this.locked) return;
    let inReach = false;
    for (const mote of this.motes) {
      if (Phaser.Math.Distance.Between(mote.x, mote.y, this.wisp.x, this.wisp.y) <= this.reach) {
        inReach = true;
        break;
      }
    }
    const ready = this.time.now >= this.gatherReadyAt;
    const kindled = reachReady(this.reach);
    const readiness = reachReadiness(this.reach);
    // Untaught players get a slow breathing edge the first time something is in
    // range; once they have pressed once the ring settles down and stops asking.
    const invite = inReach && !this.taught ? 0.18 + Math.sin(time * 0.006) * 0.12 : 0;
    const alpha = (inReach ? (ready && kindled ? 0.42 : 0.16) : 0.06) + invite;
    const reachColor = kindled ? 0xffe2a8 : 0x8fb4d8;
    this.reachRing.lineStyle(inReach ? 2 : 1, reachColor, alpha);
    this.reachRing.strokeCircle(this.wisp.x, this.wisp.y, this.reach);

    // The close halo is the hand-readable resource: complete gold means the
    // pull is ready; a spent blue arc fills only as the player moves through
    // light. It lives on the wisp, not in a detached HUD meter.
    this.reachRing.lineStyle(kindled ? 3.5 : 3, reachColor, kindled ? 0.82 : 0.58);
    this.reachRing.beginPath();
    this.reachRing.arc(
      this.wisp.x,
      this.wisp.y,
      38,
      -Math.PI / 2,
      -Math.PI / 2 + Math.PI * 2 * Math.max(0.025, readiness),
    );
    this.reachRing.strokePath();

    // A filament to each mote in reach: what the press will take, before it is pressed.
    if (!inReach) return;
    for (const mote of this.motes) {
      const d = Phaser.Math.Distance.Between(mote.x, mote.y, this.wisp.x, this.wisp.y);
      if (d > this.reach) continue;
      this.reachRing.lineStyle(1, 0xffe2a8, 0.16 + 0.26 * (1 - d / this.reach));
      this.reachRing.lineBetween(this.wisp.x, this.wisp.y, mote.x, mote.y);
    }
  }

  private baseIntensity(): number {
    return 1.6 + this.collected * 0.06;
  }

  /**
   * Make the number felt. Reach is the only stat in the game and it never gets
   * a HUD line, so it has to be legible in the light itself: a wide reach
   * burns white and streams sparks, a spent one guts down to a small cold
   * flicker. The gutter is the tell that a press just cost you something real.
   */
  private reachFeel(time: number, dt: number): number {
    const t = Phaser.Math.Clamp((this.reach - REACH_MIN) / (REACH_MAX - REACH_MIN), 0, 1);
    // Frequency only - the emitter's alpha ramp is what fades a spark out, and
    // overriding it with a constant makes the trail pop instead of dissolve.
    this.trail.frequency = 64 - t * 34;
    this.wisp.setAlpha(0.78 + t * 0.22);

    // Below a third of the range the light is running out: a fast, shallow
    // flicker on top of the slow breath, so "nearly spent" is visible before
    // it is a problem.
    if (t > 0.34) {
      this.gutter = Phaser.Math.Linear(this.gutter, 0, 1 - Math.pow(0.02, dt));
      return this.gutter;
    }
    const depth = (0.34 - t) / 0.34;
    this.gutter = Math.sin(time * 0.021) * 0.1 * depth + Math.sin(time * 0.053) * 0.06 * depth;
    return this.gutter;
  }

  update(time: number, delta: number): void {
    this.advanceWaits(delta);
    if (this.locked) return;

    const dt = delta / 1000;
    const step = dt * WISP_MAX_SPEED;
    if (this.cursors.left.isDown || this.wasd.left.isDown) this.target.x -= step;
    if (this.cursors.right.isDown || this.wasd.right.isDown) this.target.x += step;
    if (this.cursors.up.isDown || this.wasd.up.isDown) this.target.y -= step;
    if (this.cursors.down.isDown || this.wasd.down.isDown) this.target.y += step;
    this.target.x = Phaser.Math.Clamp(this.target.x, 27, WORLD_WIDTH - 27);
    this.target.y = Phaser.Math.Clamp(this.target.y, 27, WORLD_HEIGHT - 27);

    // Ease toward the target (the trailing, gliding feel), but then clamp
    // the actual distance covered this frame to WISP_MAX_SPEED. Pointer
    // input sets `target` straight to the cursor's world position with no
    // distance limit of its own, which - unclamped - let a single mouse
    // flick close far more ground per frame than the keyboard's own capped
    // step ever could, making hazard avoidance an accident of input device
    // rather than a designed difficulty curve. For ordinary small movements
    // the eased step is already under the cap, so this only ever bites the
    // extreme case, not the everyday trailing feel.
    const easeT = 1 - Math.pow(0.002, dt);
    const easedX = Phaser.Math.Linear(this.wisp.x, this.target.x, easeT);
    const easedY = Phaser.Math.Linear(this.wisp.y, this.target.y, easeT);
    let dx = easedX - this.wisp.x;
    let dy = easedY - this.wisp.y;
    const moveDist = Math.sqrt(dx * dx + dy * dy);
    const maxStep = WISP_MAX_SPEED * dt;
    if (moveDist > maxStep && moveDist > 0) {
      const scale = maxStep / moveDist;
      dx *= scale;
      dy *= scale;
    }
    this.wisp.x += dx;
    this.wisp.y += dy;
    this.applyMoonCurrents(dt);
    this.applyMoonCauseway(dt);
    this.wispLight.setPosition(this.wisp.x, this.wisp.y);
    this.hazardTrail.setPosition(0, 0);

    const breathe = Math.sin(time * 0.0007) * 0.12;
    this.pulseBoost = Phaser.Math.Linear(this.pulseBoost, 0, 1 - Math.pow(0.001, dt));
    this.wispLight.intensity = this.baseIntensity() + breathe + this.pulseBoost + this.reachFeel(time, dt);

    this.drawReachRing(time);
    this.inviteGather();

    const beforeExpiry = this.chainState;
    this.chainState = expireChain(this.chainState, time);
    if (beforeExpiry !== this.chainState) this.clearChainDisplay();
    this.drawChainBoundary(time);

    for (const h of this.hazards) {
      this.hazardTrail.emitParticleAt(h.img.x, h.img.y, 1);
    }
    this.checkHazardAlerts();

    if (time > this.graceUntil) {
      this.checkHazardCollisions();
    }
    this.collectNearbyMotes();
    if (this.levelClear) {
      this.checkBeaconArrival();
    }

    // Keep the published positions live between collect/fail events, so a
    // scripted play run can steer by them - telemetry a human player already
    // has by looking at the screen, not a capability the game itself lacks.
    const published = window.__glow;
    if (published && published.scene === "level") {
      published.wispX = Math.round(this.wisp.x);
      published.wispY = Math.round(this.wisp.y);
      for (let i = 0; i < this.hazards.length; i += 1) {
        const h = published.hazards[i];
        if (h) {
          h.x = Math.round(this.hazards[i].img.x);
          h.y = Math.round(this.hazards[i].img.y);
        }
      }
    }
  }

  /**
   * A dormant moonstone owns one broad vertical current. It pushes both the
   * light and its intended destination, so it feels like moving water rather
   * than camera shake. Waking the stone removes the force for the whole run.
   */
  private applyMoonCurrents(dt: number): void {
    this.inCurrent = false;
    for (const stone of this.echoStones) {
      if (!stone.awake) stone.current.tilePositionY += stone.pushY * 74 * dt;
      if (
        stone.awake
        || Math.abs(this.wisp.x - stone.x) > CURRENT_HALF_WIDTH
        || Math.abs(this.wisp.y - stone.y) > CURRENT_HALF_HEIGHT
      ) continue;
      this.inCurrent = true;
      const push = stone.pushY * CURRENT_PUSH_SPEED * dt;
      this.wisp.y = Phaser.Math.Clamp(this.wisp.y + push, 27, WORLD_HEIGHT - 27);
      this.target.y = Phaser.Math.Clamp(this.target.y + push, 27, WORLD_HEIGHT - 27);
    }
  }

  /**
   * An awakened moonstone points forward, not back: its luminous segment is a
   * strip of stilled water that gently carries the wisp toward the next cold
   * ring (or the beacon). Moving the target with the wisp keeps the ordinary
   * trailing controls from fighting that earned current.
   */
  private applyMoonCauseway(dt: number): void {
    for (let index = 0; index < this.echoStones.length; index += 1) {
      const stone = this.echoStones[index];
      if (!stone.awake) continue;
      const next = this.echoStones[index + 1] ?? { x: BEACON_X, y: BEACON_Y };
      const segmentX = next.x - stone.x;
      const segmentY = next.y - stone.y;
      const lengthSquared = segmentX * segmentX + segmentY * segmentY;
      const along = Phaser.Math.Clamp(
        ((this.wisp.x - stone.x) * segmentX + (this.wisp.y - stone.y) * segmentY) / lengthSquared,
        0,
        1,
      );
      const nearestX = stone.x + segmentX * along;
      const nearestY = stone.y + segmentY * along;
      if (Phaser.Math.Distance.Between(this.wisp.x, this.wisp.y, nearestX, nearestY) > CAUSEWAY_HALF_WIDTH) continue;

      const length = Math.sqrt(lengthSquared);
      const carryX = (segmentX / length) * CAUSEWAY_PUSH_SPEED * dt;
      const carryY = (segmentY / length) * CAUSEWAY_PUSH_SPEED * dt;
      this.wisp.x = Phaser.Math.Clamp(this.wisp.x + carryX, 27, WORLD_WIDTH - 27);
      this.wisp.y = Phaser.Math.Clamp(this.wisp.y + carryY, 27, WORLD_HEIGHT - 27);
      this.target.x = Phaser.Math.Clamp(this.target.x + carryX, 27, WORLD_WIDTH - 27);
      this.target.y = Phaser.Math.Clamp(this.target.y + carryY, 27, WORLD_HEIGHT - 27);
      break;
    }
  }

  private checkHazardCollisions(): void {
    for (const h of this.hazards) {
      if (Phaser.Math.Distance.Between(h.img.x, h.img.y, this.wisp.x, this.wisp.y) <= HAZARD_RADIUS) {
        this.fail();
        return;
      }
    }
  }

  private collectNearbyMotes(): void {
    for (let i = this.motes.length - 1; i >= 0; i -= 1) {
      const mote = this.motes[i];
      if (Phaser.Math.Distance.Between(mote.x, mote.y, this.wisp.x, this.wisp.y) > COLLECT_RADIUS) continue;
      this.motes.splice(i, 1);
      this.tweens.killTweensOf(mote);
      this.trail.explode(18, mote.x, mote.y);
      const pullX = this.wisp.x;
      const pullY = this.wisp.y;
      this.tweens.add({
        targets: mote,
        x: pullX,
        y: pullY,
        scale: 0.08,
        alpha: 0,
        duration: 190,
        ease: "Cubic.easeIn",
        onComplete: () => mote.destroy(),
      });
      this.takeMote("touched");
    }
  }

  /**
   * One mote becomes yours. Pulled light returns a glimmer; touched light
   * rekindles the reach. The difference is the cost of replacing travel with
   * a press, and is drawn continuously by the halo around the wisp.
   */
  private takeMote(arrival: MoteArrival): void {
    this.collected += 1;
    // The opening belongs to the pull and its cost. The inherited five-light
    // chain is a useful escalation later, but its corner timer and radiance
    // wave would introduce a second resource inside the first ten seconds.
    // The final clearing earns that extra layer; the first two levels keep the
    // chain completely quiet even for a reacher who clears level 1 in six seconds.
    const chainActive = chainActiveForLevel(this.config.index);
    if (chainActive) this.advanceChain();
    this.ambience.chime(this.collected, chainActive ? this.chainState.count : 1);
    const wasReady = reachReady(this.reach);
    this.setReach(restoreReach(this.reach, arrival));
    if (arrival === "touched") this.touchedMotes += 1;
    else this.gatheredMotes += 1;
    if (!wasReady && reachReady(this.reach)) this.kindleReach();
    this.collectionImpact();
    this.grow();
  }

  /** Completing the close halo gets one restrained world-space bloom. */
  private kindleReach(): void {
    const ring = this.add
      .circle(this.wisp.x, this.wisp.y, 38, 0xffe2a8, 0)
      .setStrokeStyle(3, 0xffe2a8, 0.86)
      .setDepth(8);
    this.tweens.add({
      targets: ring,
      radius: 92,
      alpha: 0,
      duration: 480,
      ease: "Quad.easeOut",
      onUpdate: () => ring.setPosition(this.wisp.x, this.wisp.y),
      onComplete: () => ring.destroy(),
    });
  }

  private advanceChain(): void {
    const result = advanceChain(this.chainState, this.time.now);
    this.chainState = result.state;
    if (result.released) this.releaseRadiance();
  }

  private clearChainDisplay(): void {
    this.tweens.killTweensOf(this.chainText);
    this.chainText.setAlpha(1);
    this.chainText.setText("");
    this.chainArc.clear();
  }

  private resetChain(): void {
    this.chainState = resetChain(this.chainState);
    this.clearChainDisplay();
  }

  private collectionImpact(): void {
    // Rate-limited: a gathered cascade lands four collects inside a quarter
    // second and four overlapping shakes read as a rattle, not as impact.
    if (this.time.now - this.lastShakeAt > 120) {
      this.lastShakeAt = this.time.now;
      this.cameras.main.shake(65 + this.chainState.count * 12, 0.0009 + this.chainState.count * 0.00018);
    }
    this.trail.explode(14 + this.chainState.count * 4, this.wisp.x, this.wisp.y);
    const ring = this.add.circle(this.wisp.x, this.wisp.y, 22, 0xffdfa0, 0)
      .setStrokeStyle(2 + this.chainState.count * 0.35, 0xffdfa0, 0.72).setDepth(7);
    this.tweens.add({
      targets: ring,
      radius: 54 + this.chainState.count * 13,
      alpha: 0,
      duration: 360 + this.chainState.count * 45,
      ease: "Quad.easeOut",
      onComplete: () => ring.destroy(),
    });
  }

  private releaseRadiance(): void {
    let affected = 0;
    for (const hazard of this.hazards) {
      const distance = Phaser.Math.Distance.Between(hazard.img.x, hazard.img.y, this.wisp.x, this.wisp.y);
      if (!hazard.alert || distance > RADIANCE_RADIUS) continue;
      hazard.slowUntil = this.time.now + RADIANCE_SLOW_MS;
      if (hazard.tween) hazard.tween.timeScale = RADIANCE_TIME_SCALE;
      affected += 1;
    }
    this.ambience.radiance();
    // No camera flash. A full-screen cream wash is the one effect that can
    // undo the whole art direction in 170ms, and with the reach filling a
    // chain in a single press it was firing several times a level - the
    // screenshot of this game at its best moment was a blank yellow rectangle.
    // The wave and the shadows going quiet say it without blinding anyone.
    this.cameras.main.shake(150, 0.0022);
    const wave = this.add.circle(this.wisp.x, this.wisp.y, 32, 0xffe2a8, 0.08)
      .setStrokeStyle(5, 0xffe2a8, 0.92).setDepth(8);
    this.tweens.add({
      targets: wave,
      radius: RADIANCE_RADIUS,
      alpha: 0,
      duration: 760,
      ease: "Cubic.easeOut",
      onComplete: () => wave.destroy(),
    });
    // Says its piece and goes. Left to the chain's own expiry it sat in the
    // corner for the full four-second window, which on a contact sheet is six
    // frames in a row of a game that is meant to be quiet.
    this.chainText.setText(affected > 0 ? "shadows slowed" : "");
    this.tweens.killTweensOf(this.chainText);
    this.chainText.setAlpha(1);
    if (affected > 0) {
      this.tweens.add({ targets: this.chainText, alpha: 0, duration: 500, delay: 1100 });
    }
  }

  private drawChainBoundary(time: number): void {
    this.chainArc.clear();
    if (this.chainState.count <= 0) return;
    const remaining = Phaser.Math.Clamp((this.chainState.deadline - time) / CHAIN_WINDOW_MS, 0, 1);
    const color = this.chainState.count === CHAIN_CAP ? 0xffdfa0 : 0x9fcfff;
    this.chainArc.lineStyle(3, color, 0.72);
    this.chainArc.beginPath();
    this.chainArc.arc(VIEW_WIDTH - 43, 64, 18, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * remaining);
    this.chainArc.strokePath();
    // The arc is the whole readout - a filling ring in the corner, no number to
    // read. The words are kept for the one moment they mean something.
  }

  /** How many motes open the beacon this level (defensively never above what was actually placed). */
  private requiredMotes(): number {
    return Math.min(this.config.requiredMotes, this.totalMotes);
  }

  private grow(): void {
    const required = this.requiredMotes();
    const moteProgress = this.collected / required;
    const progress = this.echoStones.length > 0
      ? Phaser.Math.Clamp(moteProgress * 0.78 + (this.echoesAwake() / this.echoStones.length) * 0.22, 0, 1)
      : Phaser.Math.Clamp(moteProgress, 0, 1);
    this.beacon.setAlpha(0.05 + progress * 0.8);
    this.beaconLight.intensity = progress * 1.4;

    // A movement-only first pass can gather every Moonwell mote without ever
    // repeating the click that started the game. Once loose light is no longer
    // the problem, stop fading the lesson: name the blocked objective and make
    // every unresolved interaction radius breathe. A human gets a clear next
    // action; the stones are still real required switches, never auto-cleared.
    if (
      this.echoStones.length > 0
      && this.collected >= required
      && this.echoesAwake() < this.echoStones.length
      && !this.echoReminderShown
    ) {
      this.echoReminderShown = true;
      this.echoHint?.setText("the beacon waits · press inside each pale ring").setAlpha(0.92);
      for (const stone of this.echoStones) {
        if (stone.awake) continue;
        stone.light.intensity = 0.6;
        this.tweens.add({
          targets: stone.ring,
          alpha: { from: 0.32, to: 1 },
          duration: 760,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        });
      }
    }

    // The beacon opens at the required count - everything past it is the
    // player's own choice: bank the level now, or brave the guarded pockets
    // for the remaining motes and the flawless variant.
    if (this.collected >= required && this.echoesAwake() >= this.echoStones.length && !this.levelClear) {
      this.levelClear = true;
      this.ambience.beaconOpen();
      this.showOpenLine();
      this.beaconPulse(1.12);
    }

    if (this.collected >= this.totalMotes && !this.flawlessNow) {
      this.flawlessNow = true;
      this.beacon.setAlpha(1);
      this.beaconLight.intensity = 2.2;
      this.beaconLight.setColor(0xffe9c0);
      this.beaconPulse(1.2);
    }

    this.updateHud();
    this.reportState();
  }

  private beaconPulse(to: number): void {
    this.tweens.killTweensOf(this.beacon);
    this.beacon.setScale(1);
    this.tweens.add({
      targets: [this.beacon],
      scale: { from: 1, to },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  /** One quiet serif line under the level card, the moment the beacon opens. */
  private showOpenLine(): void {
    this.tweens.killTweensOf(this.openLine);
    this.openLine.setAlpha(0);
    this.tweens.add({
      targets: this.openLine,
      alpha: { from: 0, to: 0.85 },
      duration: 700,
      yoyo: true,
      hold: 1900,
      ease: "Sine.easeInOut",
    });
  }

  private checkBeaconArrival(): void {
    if (this.locked) return;
    if (Phaser.Math.Distance.Between(this.wisp.x, this.wisp.y, BEACON_X, BEACON_Y) <= BEACON_RADIUS) {
      this.completeLevel();
    }
  }

  /**
   * Scene-owned waits advance from the same deltas as play. Empty-target
   * tweens can complete immediately under frame-exact replay, while Phaser's
   * delayedCall has missed callbacks on some hosts. This tiny queue is both
   * deterministic and continues during the movement lock after a hit.
   */
  private after(ms: number, onComplete: () => void): void {
    this.waits.push({ remainingMs: ms, onComplete });
  }

  private advanceWaits(delta: number): void {
    const ready: Array<() => void> = [];
    const pending: typeof this.waits = [];
    for (const wait of this.waits) {
      wait.remainingMs -= delta;
      if (wait.remainingMs <= 0) ready.push(wait.onComplete);
      else pending.push(wait);
    }
    this.waits = pending;
    for (const onComplete of ready) onComplete();
  }

  /** The player touched a shadow-wisp: snuff the light, lose this level's progress, try again. */
  private fail(): void {
    this.locked = true;
    this.resets += 1;
    this.ambience.hit();
    // The dark closes over you. A camera flash - even a violet one - answers
    // "your light just went out" by turning the whole screen ON, which is
    // exactly backwards in this game and reads as a rendering fault at 720p.
    this.tweens.killTweensOf(this.deathVeil);
    this.deathVeil.setAlpha(0);
    this.tweens.add({
      targets: this.deathVeil,
      alpha: { from: 0, to: 0.82 },
      duration: 200,
      yoyo: true,
      hold: 190,
      ease: "Sine.easeOut",
    });
    this.cameras.main.shake(220, 0.006);

    this.tweens.add({
      targets: this.wispLight,
      intensity: 0.05,
      radius: 90,
      duration: 260,
      ease: "Quad.easeIn",
    });
    this.wisp.setScale(0.2);
    this.reachRing.clear();
    for (const mote of this.incoming) this.tweens.killTweensOf(mote);
    this.resetChain();
    this.reportState();

    this.after(560, () => {
      this.target.set(this.respawnX, this.respawnY);
      this.wisp.setPosition(this.respawnX, this.respawnY);
      this.wispLight.setPosition(this.respawnX, this.respawnY);
      // What a shadow takes is your light, not your work. The old fail wiped
      // the level's motes and started it again, which at twenty seconds in is
      // the moment a player stops playing - and it punished the one thing the
      // round wants them doing, which is going near a shadow to reach past it.
      // Now the sting is the reach itself: it is snuffed to the floor and only
      // motes bring it back, so a death late in a level means finishing that
      // level nearly blind, walking back across ground you already lit. Same
      // currency as the press, so there is one number in the game and dying,
      // spending and collecting all speak it.
      this.tweens.killTweensOf(this.wispLight);
      this.setReach(REACH_MIN);
      this.wispLight.intensity = this.baseIntensity();
      this.gatherReadyAt = 0;
      this.updateHud();
      this.reportState();
      this.graceUntil = this.time.now + RESPAWN_GRACE_MS;
      this.locked = false;
    });
  }

  private completeLevel(): void {
    this.locked = true;
    const wasFlawless = this.collected >= this.totalMotes;
    const flawless = this.flawlessLevels + (wasFlawless ? 1 : 0);
    this.ambience.levelComplete(wasFlawless);
    // The arrival is a swell, not a switch. A camera flash paints the frame
    // solid at full alpha before it fades, so the one frame in three that a
    // contact sheet catches of this game's best moment was a cream rectangle -
    // the same fault as the old radiance flash and the old death flash. An
    // additive veil to 0.42 and a beacon that blooms says "you made it" while
    // the forest is still visible behind it.
    this.tweens.killTweensOf(this.arrivalVeil);
    this.arrivalVeil.setAlpha(0);
    this.tweens.add({
      targets: this.arrivalVeil,
      alpha: { from: 0, to: 0.42 },
      duration: 200,
      yoyo: true,
      hold: 90,
      ease: "Sine.easeOut",
    });
    // beaconPulse() leaves an infinite yoyo running on the same property.
    this.tweens.killTweensOf(this.beacon);
    this.tweens.add({
      targets: this.beacon,
      scale: { from: this.beacon.scale, to: this.beacon.scale * 1.8 },
      duration: 520,
      ease: "Cubic.easeOut",
    });
    this.tweens.add({
      targets: this.beaconLight,
      intensity: 3.4,
      radius: 520,
      duration: 420,
      ease: "Cubic.easeOut",
    });
    this.cameras.main.fadeOut(520, 8, 7, 14);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      const next = this.config.index + 1;
      if (levelFor(next)) {
        this.scene.start("level", {
          levelIndex: next,
          ambience: this.ambience,
          resets: this.resets,
          flawless,
          taught: this.taught,
        });
      } else {
        this.scene.start("ending", { ambience: this.ambience, resets: this.resets, flawless });
      }
    });
  }

  private updateHud(): void {
    const moteSegment = this.flawlessNow
      ? `motes ${this.collected}/${this.totalMotes} · flawless`
      : this.levelClear
        ? `motes ${this.collected}/${this.totalMotes} · beacon open`
        : `motes ${this.collected}/${this.totalMotes} · beacon at ${this.requiredMotes()}`;
    const echoSegment = this.echoStones.length > 0
      ? `   moonstones ${this.echoesAwake()}/${this.echoStones.length}`
      : "";
    this.hud.setText(`LEVEL ${this.config.index}/${LEVELS.length}   ${moteSegment}${echoSegment}   resets ${this.resets}`);
  }

  private announceReady(): void {
    document.body.dataset.gameReady = "true";
    this.reportState();
  }

  private reportState(): void {
    window.__glow = {
      ready: true,
      scene: "level",
      collected: this.collected,
      remaining: this.motes.length,
      glowRadius: this.wispLight.radius,
      lightsActive: this.lights.active,
      level: this.config.index,
      resets: this.resets,
      required: this.requiredMotes(),
      beaconOpen: this.levelClear,
      flawless: this.flawlessLevels,
      wispX: Math.round(this.wisp.x),
      wispY: Math.round(this.wisp.y),
      motes: this.motes.map((m) => ({ x: Math.round(m.x), y: Math.round(m.y) })),
      hazards: this.hazards.map((h) => ({ x: Math.round(h.img.x), y: Math.round(h.img.y) })),
      reach: Math.round(this.reach),
      gathers: this.gathers,
      gatherReady: reachReady(this.reach),
      deniedGathers: this.deniedGathers,
      touchedMotes: this.touchedMotes,
      gatheredMotes: this.gatheredMotes,
      chain: this.chainState.count,
      chainRemainingMs: Math.max(0, Math.round(this.chainState.deadline - this.time.now)),
      radianceWaves: this.chainState.waves,
      slowedHazards: this.hazards.filter((h) => h.slowUntil > this.time.now).length,
      echoesAwake: this.echoesAwake(),
      echoesRequired: this.echoStones.length,
      inCurrent: this.inCurrent,
      anchorX: Math.round(this.respawnX),
      anchorY: Math.round(this.respawnY),
    };
  }
}
